import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { GetLogo, loginUser } from "../../apis/api/AuthApi";
import { backendUrl } from "../../apis/api/config";
import Lodderr from "../../components/Lodder/Lodder";
import "./Login.css";

const Login = () => {
  const { register, handleSubmit, formState:{ errors } } = useForm();
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const { loading, companyLogo } = useSelector(s => s.AuthSlice);

  useEffect(() => { dispatch(GetLogo()); }, [dispatch]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error === 'not_registered') {
      toast.error('No account found for this Google email. Please register first.');
    } else if (error === 'login_failed') {
      toast.error('Google sign-in failed. Please try again or use email login.');
    }
  }, []);

  const onSubmit = async (data) => {
    const fd = new FormData();
    fd.append("email",      data.email);
    fd.append("password",   data.password);
    fd.append("rememberMe", data.rememberMe);
    fd.append("loginFrom",  "Web");

    const response = await dispatch(loginUser(fd));

    if (response?.payload?.status === "success") {
      const member     = response.payload.data.member;
      const nameParts  = [member?.first_name, member?.middle_name, member?.last_name]
        .filter(p => p && p !== "null" && p !== "undefined");

      Cookies.set("token",      response.payload.token);
      Cookies.set("userID",     member?.id);
      Cookies.set("company_id", member?.company_id);
      Cookies.set("access",     JSON.stringify(response.payload.data.membership_plan));
      Cookies.set("user", JSON.stringify({
        fullname:     nameParts.join(" ") || "Guest User",
        company_name: member?.company_name ?? "No Company",
        member_logo:  member?.member_logo ?? "/gray-avatar-icon-vector-illustration_276184-163.avif",
      }));

      toast.success("Login Successful");
      navigate("/dashboard");
    } else if (response?.payload?.status === "error") {
      toast.error(response?.payload?.message);
    }
  };

  return (
    <>
      {loading && <Lodderr />}
      <div className="lg-wrap">

        {/* ── LEFT — logistics image ── */}
        <div className="lg-left">
          <img
            src={`${process.env.PUBLIC_URL}/images/logistic.jpg`}
            className="lg-left-img" alt="Logistics"
            onError={e => { e.target.style.display="none"; }}
          />
          <div className="lg-left-overlay" />
          <motion.div className="lg-left-content"
            initial={{ opacity:0, y:24 }} animate={{ opacity:1, y:0 }}
            transition={{ duration:.6, ease:[.22,.68,0,1.2] }}>
            <span className="lg-left-badge">
              <i className="bx bx-world"></i> Global Logistics Network
            </span>
            <h1 className="lg-left-title">
              Frictionless freight,<br />from dock to doorstep.
            </h1>
            <p className="lg-left-sub">
              Manage freight requests, invoices, member directory, events and your
              community — all in one modern workspace.
            </p>
            <div className="lg-left-stats">
              {[
                { val:"54.5K", lbl:"Network members" },
                { val:"99.6%", lbl:"Delivery visibility" },
                { val:"58 Days", lbl:"Avg transit time" },
              ].map(s => (
                <motion.div key={s.lbl} className="lg-stat"
                  initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }}
                  transition={{ delay:.3 }}>
                  <div className="lg-stat-val">{s.val}</div>
                  <div className="lg-stat-lbl">{s.lbl}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ── RIGHT — form ── */}
        <motion.div className="lg-right"
          initial={{ opacity:0, x:30 }} animate={{ opacity:1, x:0 }}
          transition={{ duration:.5, ease:[.22,.68,0,1.2] }}>

          <div className="lg-right-inner">
            {/* Logo */}
            <div className="lg-logo-wrap">
              <img
                src={companyLogo ? companyLogo.replace("/thumb","") : `${process.env.PUBLIC_URL}/company-logo-design.jpg`}
                className="lg-logo-img" alt="Logo"
                onError={e => { e.target.onerror=null; e.target.src=`${process.env.PUBLIC_URL}/company-logo-design.jpg`; }}
              />
            </div>

            <h1 className="lg-heading">Welcome back</h1>
            <p className="lg-sub-text">Sign in to your logistics member portal.</p>

            <form className="lg-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="lg-field">
                <label className="lg-label">Email address</label>
                <input type="email" placeholder="you@company.com"
                  className={`lg-input ${errors.email?"is-invalid":""}`}
                  {...register("email",{
                    required:"Email is required",
                    pattern:{ value:/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/, message:"Invalid email" }
                  })} />
                {errors.email && <span className="lg-err">{errors.email.message}</span>}
              </div>

              <div className="lg-field">
                <label className="lg-label">Password</label>
                <input type="password" placeholder="Enter password"
                  className={`lg-input ${errors.password?"is-invalid":""}`}
                  {...register("password",{ required:"Password is required" })} />
                {errors.password && <span className="lg-err">{errors.password.message}</span>}
              </div>

              <div className="lg-row">
                <label className="lg-check">
                  <input type="checkbox" {...register("rememberMe")} />
                  <span>Remember me</span>
                </label>
                <a className="lg-link" href="#!">Forgot password?</a>
              </div>

              <button type="submit" className="lg-btn" disabled={loading}>
                <i className="bx bxs-lock-open"></i>
                {loading ? "Signing in…" : "Sign in"}
              </button>

              { /* <button
                type="button"
                className="google-btn"
                onClick={() => {
                  window.location.href = `${backendUrl}/auth/google`;
                }}
              >
                <img
                  src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg"
                  alt="google"
                  className="google-icon"
                />
                Login with Google
              </button> */}

              <div className="lg-foot">
                <span>Don't have an account?</span>
                <Link className="lg-link" to="/signUp">Create account</Link>
              </div>
            </form>
          </div>
        </motion.div>

      </div>
    </>
  );
};

export default Login;
